import React, { useState, useCallback, useRef, useEffect } from 'react'; // Dodano useCallback, useRef, useEffect
import { Pencil, Check, X, Trash2, PlusCircle, Save, FilePenLine, FileText, Users, List, Sparkles, Copy, MessageSquare, Eye, Edit3, Wand2, HelpCircle } from 'lucide-react';
import debounce from 'lodash.debounce'; // Import debounce

// Komponent do renderowania pojedynczego wiersza edycji listy gości
const GuestListEditorRow = ({ fileId, isOpen, isLoading, text, onTextChange, onSave, isVisible }) => {
  const [localText, setLocalText] = useState(text);

  // Synchronizuj localText z propsem text, gdy ten się zmieni (np. po załadowaniu/zapisie)
  useEffect(() => {
    setLocalText(text);
  }, [text]);

  if (!isOpen || isVisible === false) return null;

  const handleChange = (e) => {
    const newText = e.target.value;
    setLocalText(newText); // Aktualizuj lokalny stan natychmiast
    onTextChange(fileId, newText); // Wywołaj debounced funkcję aktualizacji stanu nadrzędnego
  };

  return (
    <tr className={`guest-list-row ${isOpen ? 'visible' : ''}`}>
      <td colSpan={3}>
        <div className="guest-list-content">
          {isLoading ? (
            <p style={{ color: "#999", margin: 0 }}>Wczytywanie / zapisywanie...</p>
          ) : (
            <>
              <textarea
                rows={6}
                value={localText} // Użyj localText
                onChange={handleChange} // Użyj lokalnego handlera
                style={{
                  width: "100%",
                  padding: 8,
                  boxSizing: "border-box",
                  fontFamily: "inherit"
                }}
              />
              <button
                className="client-btn"
                style={{ marginTop: 10, display: 'block' }}
                onClick={() => onSave(fileId)}
                // {...tooltipProps("Zapisz wprowadzoną listę gości")} // Tooltip props need to be passed down if needed
              >
                Zapisz listę gości
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

// Komponent do renderowania komórki z nazwą pliku (z możliwością edycji)
const FileNameCell = ({ fileObj, isEditing, tempName, onTempNameChange, onStartEdit, onSave, onCancel, isChild, tooltipProps }) => {
  if (isEditing) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
        <input
          type="text"
          value={tempName}
          onChange={(e) => onTempNameChange(e.target.value)}
          className="client-panel-filename-input"
          autoFocus
          onKeyDown={(e) => { if (e.key === 'Enter') onSave(); if (e.key === 'Escape') onCancel(); }}
        />
        <button onClick={onSave} className="client-btn-icon client-btn-icon-small" title="Zapisz">
          <Check size={14} />
        </button>
        <button onClick={onCancel} className="client-btn-icon client-btn-icon-small" title="Anuluj">
          <X size={14} />
        </button>
      </div>
    );
  }

  return (
    <>
      {fileObj.fileName}
      {isChild && (
        <span style={{ color: "orangered", marginLeft: 6 }}>
          (z listą gości)
        </span>
      )}
      <button
        onClick={() => onStartEdit(fileObj)}
        className="client-btn-icon client-btn-icon-small"
        style={{ marginLeft: '8px' }}
        {...tooltipProps("Edytuj nazwę pliku")}
      >
        <Pencil size={12} />
      </button>
    </>
  );
};

// Komponent do renderowania komórki z ilością (z bezpośrednią edycją i debouncingiem)
const QuantityCell = ({ fileObj, onUpdateQuantity, isUpdating, tooltipProps }) => {
  const isChild = !!fileObj.parentFileId;
  const [localQuantity, setLocalQuantity] = useState(String(fileObj.quantity || '1'));

  // Aktualizuj stan lokalny, jeśli zmieni się ilość z propsów (np. po zapisie z zewnątrz)
  // Unikamy aktualizacji, gdy komponent sam jest w trakcie aktualizacji, by nie nadpisać wpisywanej wartości
  useEffect(() => {
    if (!isUpdating) {
      setLocalQuantity(String(fileObj.quantity || '1'));
    }
  }, [fileObj.quantity, isUpdating]);

  // Użyj useRef, aby przechować funkcję debounced, co zapobiega jej ponownemu tworzeniu
  const debouncedSave = useRef(
    debounce((fileId, newQuantity) => {
      const quantityNum = parseInt(newQuantity, 10);
      if (!isNaN(quantityNum) && quantityNum >= 1) {
        onUpdateQuantity(fileId, quantityNum);
      } else {
        // Jeśli wartość jest nieprawidłowa, nie rób nic lub przywróć starą wartość.
        // Aktualizacja w useEffect zajmie się przywróceniem poprawnej wartości z propsów.
      }
    }, 800)
  ).current;

  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalQuantity(newValue); // Aktualizuj stan lokalny natychmiast
    debouncedSave(fileObj.id, newValue); // Wywołaj debounced funkcję
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSave.cancel(); // Anuluj oczekujące wywołanie debounce przy odmontowywaniu
    };
  }, [debouncedSave]);


  if (isChild) {
    return <span {...tooltipProps("Ilość ustalana automatycznie na podstawie listy gości")}>{fileObj.quantity}</span>;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', minWidth: '70px' }}>
      <input
        type="number"
        min="1"
        value={localQuantity}
        onChange={handleChange}
        className="client-panel-quantity-input"
        style={{ width: '60px', textAlign: 'right', padding: '2px 5px' }}
        disabled={isUpdating}
        {...tooltipProps("Wpisz żądaną ilość. Zapis nastąpi automatycznie.")}
      />
      {isUpdating && <div className="popup-spinner popup-spinner-button-tiny" style={{ marginLeft: '5px' }}></div>}
    </div>
  );
};



// Główny komponent tabeli projektów
const ClientProjectTable = ({
  files,
  statuses,
  // Usunięto personalizationAvailability
  editingFileId,
  tempFileName,
  setTempFileName,
  // Usunięto stany i funkcje edycji ilości
  updatingQuantityFileId, // Nadal potrzebne do QuantityCell
  guestsOpenMap,
  guestsTextMap,
  setGuestsTextMap,
  guestsLoadingFileId,
  guestEditAreaVisibleMap, // NOWY PROP
  duplicatingFileId,
  deletingFileId,
  tooltipProps,
  onEditFileNameClick,
  onCancelEditFileName,
  onSaveFileName,
  onUpdateQuantity, // Nowa funkcja do przekazania do QuantityCell
  onOpenEditor,
  onPreviewPdf,
  onToggleGuests,
  onFetchGuests,
  onSaveGuests,
  onGeneratePersonalized,
  onDuplicateProject,
  onDeleteProject,
  onOpenSupportPopup, // Nowy prop
}) => {

  // Debounced function to update guestsTextMap in ClientOrderPanel
  const debouncedSetGuestsTextMap = useCallback(
    debounce((fileId, newText) => {
      setGuestsTextMap(prev => ({ ...prev, [fileId]: newText }));
    }, 300), // Adjust debounce time as needed (e.g., 300ms)
    [setGuestsTextMap] // setGuestsTextMap should be stable
  );

  const handleGuestTextChange = (fileId, newText) => {
    // Update local state immediately for responsiveness of the textarea
    // This requires GuestListEditorRow to manage its own immediate text state
    // For now, we'll just call the debounced updater.
    // If textarea becomes laggy, GuestListEditorRow will need its own local state.
    debouncedSetGuestsTextMap(fileId, newText);
  };

  const handleSaveGuests = (fileId) => {
    const fileObj = files.find(f => f.id === fileId);
    if (fileObj) {
      // onSaveGuests jest już funkcją z ClientOrderPanel, która przyjmuje fileObj i callback
      // więc przekazujemy ją bezpośrednio. ClientOrderPanel przekaże swój callback (handleGuestSaveSuccess)
      // do useClientOrderActions, a ten wywoła go po sukcesie.
      onSaveGuests(fileObj); 
    }
  };

  const handleToggleGuests = (fileObj) => {
    const fileId = fileObj.id;
    const willBeOpen = !guestsOpenMap[fileId];
    onToggleGuests(fileId, willBeOpen); // Inform parent about the toggle
    if (willBeOpen) {
      onFetchGuests(fileObj); // Fetch guests only when opening
    }
  };


  return (
    <table className="client-panel-table">
      <thead>
        <tr>
          <th>Nazwa pliku</th>
          <th>Ilość</th> {/* Nowa kolumna */}
          <th>Aktualny Status Pliku</th>
          <th>Akcje</th>
        </tr>
      </thead>
      <tbody>
        {files.map((fileObj) => {
          const isPdf = fileObj.fileName.toLowerCase().endsWith(".pdf");
          const st = statuses.find((x) => x.id === fileObj.statusId);
          const statusText = st ? st.name : "(brak)";
          const isChild = !!fileObj.parentFileId;
          const loadingThisGuestFile = guestsLoadingFileId === fileObj.id;
          const canGenerate = isPdf && !isChild && fileObj.hasPersonalization; // Generowanie tylko jeśli jest personalizacja
          const isGuestListOpen = !!guestsOpenMap[fileObj.id];
          const currentGuestText = guestsTextMap[fileObj.id] || "";
          // const isPersonalizationAvailable = personalizationAvailability[fileObj.id] === true; // Już niepotrzebne
          const isQuantityUpdating = updatingQuantityFileId === fileObj.id;
          const isGuestEditAreaVisible = guestEditAreaVisibleMap[fileObj.id] !== false; // Domyślnie true

          return (
            <React.Fragment key={fileObj.id}>
              <tr>
                {/* Komórka z nazwą pliku */}
                <td>
                  <FileNameCell
                    fileObj={fileObj}
                    isEditing={editingFileId === fileObj.id}
                    tempName={tempFileName}
                    onTempNameChange={setTempFileName}
                    onStartEdit={onEditFileNameClick}
                    onSave={onSaveFileName}
                    onCancel={onCancelEditFileName}
                    isChild={isChild}
                    tooltipProps={tooltipProps}
                  />
                </td>
                {/* Komórka z ilością */}
                <td>
                  <QuantityCell
                    fileObj={fileObj}
                    onUpdateQuantity={onUpdateQuantity} // Przekazanie funkcji aktualizacji
                    isUpdating={isQuantityUpdating}
                    tooltipProps={tooltipProps}
                  />
                </td>
                {/* Komórka ze statusem */}
                <td>{statusText}</td>
                {/* Komórka z akcjami */}
                <td>
                  {isPdf ? (
                    <>
                      <button
                        className="client-btn"
                        onClick={() => onOpenEditor(fileObj.downloadKey)}
                        {...tooltipProps("Otwórz edytor PDF, aby modyfikować projekt")}
                      >
                        <FilePenLine size={16} style={{ marginRight: '4px' }} /> Edytuj projekt
                      </button>
                      <button
                        className="client-btn-outlined"
                        onClick={() => onPreviewPdf(fileObj.downloadKey)}
                        {...tooltipProps("Pobierz podgląd projektu w formacie PDF")}
                      >
                        <Eye size={16} style={{ marginRight: '4px' }} /> Podgląd PDF
                      </button>

                      {/* Przycisk "Lista gości" */}
                      {!isChild && isPdf && fileObj.hasPersonalization && ( // Sprawdzamy flagę z obiektu pliku
                        <button
                          className="client-btn-outlined"
                          onClick={() => handleToggleGuests(fileObj)}
                          {...tooltipProps(isGuestListOpen ? "Ukryj panel edycji listy gości" : "Pokaż panel edycji listy gości dla tego projektu")}
                        >
                          {isGuestListOpen ? <><Users size={16} style={{ marginRight: '4px' }} /> Ukryj gości</> : <><List size={16} style={{ marginRight: '4px' }} /> Lista gości</>}
                        </button>
                      )}

                      {/* Przycisk generowania */}
                      {canGenerate && ( // canGenerate już sprawdza hasPersonalization
                        <button
                          className="client-btn-outlined"
                          style={{ marginLeft: 8, backgroundColor: "#f0c14b" }}
                          onClick={() => onGeneratePersonalized(fileObj)}
                          {...tooltipProps("Wygeneruj nowy projekt zawierający wszystkie elementy z listy gości")}
                        >
                          <Sparkles size={16} style={{ marginRight: '4px' }} /> Generuj projekt z listą gości
                        </button>
                      )}

                      {/* Przycisk "Duplikuj" */}
                      {!isChild && (
                        <button
                          className="client-btn-outlined"
                          style={{ marginLeft: 8 }}
                          onClick={() => onDuplicateProject(fileObj)}
                          disabled={duplicatingFileId === fileObj.id}
                          {...tooltipProps("Utwórz kopię tego projektu, aby przygotować inną wersję")}
                        >
                          {duplicatingFileId === fileObj.id ? "Duplikowanie..." : <><Copy size={16} style={{ marginRight: '4px' }} /> Duplikuj dla innej treści</>}
                        </button>
                      )}

                      {/* Przycisk "Poproś o zmiany" */}
                      {!isChild && isPdf && (
                        <button
                          className="client-btn-outlined"
                          style={{ marginLeft: 8, marginTop: 5 }} // Dodano marginTop dla lepszego układu
                          onClick={() => onOpenSupportPopup(fileObj)}
                          {...tooltipProps("Poproś o pomoc grafika lub modyfikację projektu przez AI")}
                        >
                          <MessageSquare size={16} style={{ marginRight: '4px' }} /> Poproś o zmiany
                        </button>
                      )}

                      {/* Przycisk "Usuń" - przeniesiony na koniec */}
                      <button
                        className="client-btn-icon client-btn-icon-delete"
                        style={{ marginLeft: 8 }}
                        onClick={() => onDeleteProject(fileObj)}
                        disabled={deletingFileId === fileObj.id}
                        {...tooltipProps("Usuń ten projekt z zamówienia")}
                      >
                        {deletingFileId === fileObj.id ? (
                          <div className="popup-spinner popup-spinner-button-small"></div>
                        ) : (
                          <Trash2 size={16} />
                        )}
                      </button>
                    </>
                  ) : (
                    <span className="client-file-disabled">Brak akcji</span>
                  )}
                </td>
              </tr>

              {/* Wiersz edycji listy gości */}
              <GuestListEditorRow
                fileId={fileObj.id}
                isOpen={isGuestListOpen && isPdf && !isChild}
                isLoading={loadingThisGuestFile}
                text={currentGuestText}
                onTextChange={handleGuestTextChange}
                onSave={handleSaveGuests}
                isVisible={isGuestEditAreaVisible} // Przekazanie widoczności
              />
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
};

export default ClientProjectTable;
